import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  Vibration,
  View,
} from 'react-native';

import { GlassButton } from '../../components/ui/GlassButton';
import { APP_NAME } from '../../config/branding';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import type { BudgetUser } from '../../types/auth';
import { authService } from '../Auth/services/authService';

const CONFIRM_PHRASE = 'DELETE';
const INTRO_DELAY_MS = 2500;
const HOLD_SECONDS = 5;

const ACKS: { key: string; label: string }[] = [
  { key: 'signin', label: 'Sign-in will stop on iPhone, Android, and the web' },
  { key: 'softdelete', label: 'Records stay stored internally — this is a soft delete, not a wipe' },
  { key: 'billing', label: 'I will cancel App Store, Play, or Stripe billing myself' },
];

type Phase = 'intro' | 'acks' | 'type' | 'done';

type Props = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: BudgetUser | null;
  logout: () => Promise<void>;
  onSignIn: () => void;
  onDone: () => void;
};

function hapticError(): void {
  if (Platform.OS === 'web') return;
  Vibration.vibrate([0, 50, 40, 50]);
}

function hapticSuccess(): void {
  if (Platform.OS === 'web') return;
  Vibration.vibrate(40);
}

function identitiesMatch(typed: string, expected: string): boolean {
  const a = typed.trim();
  const b = expected.trim();
  if (!b || !a) return false;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  const digitsA = a.replace(/\D/g, '');
  const digitsB = b.replace(/\D/g, '');
  return digitsA.length >= 7 && digitsA === digitsB;
}

export function DeleteAccountConfirmFlow({
  isAuthenticated,
  isLoading,
  user,
  logout,
  onSignIn,
  onDone,
}: Props) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [introReady, setIntroReady] = useState(false);
  const [introLeft, setIntroLeft] = useState(Math.ceil(INTRO_DELAY_MS / 1000));
  const [acks, setAcks] = useState<Record<string, boolean>>({});
  const [identityTyped, setIdentityTyped] = useState('');
  const [phraseTyped, setPhraseTyped] = useState('');
  const [holdLeft, setHoldLeft] = useState(HOLD_SECONDS);
  const [holdReady, setHoldReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitHint, setSubmitHint] = useState<string | null>(null);

  const accountLabel = user?.email || user?.phoneNumber || user?.name || '';
  const identityKind = user?.email ? 'email' : user?.phoneNumber ? 'phone number' : 'name';
  const ackCount = ACKS.filter((item) => acks[item.key]).length;
  const acksComplete = ackCount === ACKS.length;
  const identityOk = identitiesMatch(identityTyped, accountLabel);
  const phraseOk = phraseTyped.trim().toUpperCase() === CONFIRM_PHRASE;
  const typeReady = identityOk && phraseOk && holdReady;

  const stepIndex = phase === 'intro' ? 0 : phase === 'acks' ? 1 : 2;

  const identityHint = useMemo(() => {
    if (!identityTyped.trim()) return `Type your ${identityKind} exactly as shown above.`;
    return identityOk ? 'Matches your account.' : 'Doesn’t match yet — check spelling and digits.';
  }, [identityKind, identityOk, identityTyped]);

  const phraseHint = useMemo(() => {
    if (!phraseTyped.trim()) return `Type ${CONFIRM_PHRASE} in capital letters.`;
    return phraseOk ? `${CONFIRM_PHRASE} accepted.` : `Must be exactly ${CONFIRM_PHRASE}.`;
  }, [phraseOk, phraseTyped]);

  useEffect(() => {
    if (phase !== 'intro') {
      setIntroReady(true);
      setIntroLeft(0);
      return;
    }
    setIntroReady(false);
    setIntroLeft(Math.ceil(INTRO_DELAY_MS / 1000));
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const leftMs = Math.max(0, INTRO_DELAY_MS - (Date.now() - startedAt));
      setIntroLeft(Math.ceil(leftMs / 1000));
      if (leftMs === 0) {
        setIntroReady(true);
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (!identityOk || !phraseOk || phase !== 'type') {
      setHoldLeft(HOLD_SECONDS);
      setHoldReady(false);
      return;
    }
    setHoldLeft(HOLD_SECONDS);
    setHoldReady(false);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const left = Math.max(0, HOLD_SECONDS - Math.floor((Date.now() - startedAt) / 1000));
      setHoldLeft(left);
      if (left === 0) {
        setHoldReady(true);
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, [identityOk, phase, phraseOk]);

  const toggleAck = useCallback((key: string) => {
    setAcks((prev) => ({ ...prev, [key]: !prev[key] }));
    setError(null);
  }, []);

  const runDelete = useCallback(async () => {
    if (!identityOk) {
      setError(`Type your ${identityKind} exactly.`);
      hapticError();
      return;
    }
    if (!phraseOk) {
      setError(`Type ${CONFIRM_PHRASE} to confirm.`);
      hapticError();
      return;
    }
    if (!holdReady) {
      setError(`Wait ${holdLeft}s after both fields match.`);
      hapticError();
      return;
    }
    setBusy(true);
    setError(null);
    setSubmitHint('Deactivating sign-in…');
    try {
      await authService.deleteAccount();
      hapticSuccess();
      setSubmitHint('Sign-in is off. Signing you out…');
      setPhase('done');
      await logout();
    } catch (err) {
      hapticError();
      setSubmitHint(null);
      setError(err instanceof Error ? err.message : 'Could not deactivate account');
    } finally {
      setBusy(false);
    }
  }, [holdLeft, holdReady, identityKind, identityOk, logout, phraseOk]);

  if (phase === 'done') {
    return (
      <View style={styles.panel}>
        <Text style={styles.feedbackOk}>Account deactivated</Text>
        <Text style={styles.sectionTitle}>Sign-in is off</Text>
        <Text style={styles.body}>
          This {APP_NAME} login can no longer open the app on iPhone, Android, or the web. Push
          notifications for this account are stopped. Associated records remain stored internally
          (soft delete). We do not permanently erase that data. Cancel any App Store, Play, or
          Stripe subscription separately — deactivating the account does not stop billing.
        </Text>
        <GlassButton variant="primary" onPress={onDone}>
          Back to {APP_NAME}
        </GlassButton>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.panel}>
        <ActivityIndicator color={colors.accent} />
      </View>
    );
  }

  if (!isAuthenticated) {
    return (
      <View style={styles.panel}>
        <Text style={styles.sectionTitle}>Sign in required</Text>
        <Text style={styles.body}>
          Sign in with the Google, Apple, or phone login on the {APP_NAME} account you want to
          deactivate. After sign-in you return here to complete three confirmations.
        </Text>
        <GlassButton variant="primary" onPress={onSignIn}>
          Sign in to continue
        </GlassButton>
      </View>
    );
  }

  return (
    <View style={styles.panel}>
      <Text style={styles.accountMeta}>Signed in as {accountLabel || 'your account'}</Text>
      <View style={styles.stepsRail} accessibilityRole="progressbar">
        {['Understand', 'Acknowledge', 'Type to confirm'].map((label, index) => {
          const active = index === stepIndex;
          const complete = index < stepIndex;
          return (
            <View key={label} style={styles.stepChip}>
              <Text
                style={[
                  styles.stepChipIndex,
                  active && styles.stepChipIndexActive,
                  complete && styles.stepChipIndexDone,
                ]}
              >
                {complete ? '✓' : index + 1}
              </Text>
              <Text style={[styles.stepChipLabel, active && styles.stepChipLabelActive]}>
                {label}
              </Text>
            </View>
          );
        })}
      </View>

      {phase === 'intro' ? (
        <>
          <Text style={styles.sectionTitle}>Confirmation 1 of 3</Text>
          <Text style={styles.body}>
            This turns off sign-in and push notifications for this {APP_NAME} account. You will
            not be able to use the app with this login afterward. Records stay stored (soft
            delete). Billing is separate.
          </Text>
          <Text style={introReady ? styles.feedbackOk : styles.feedbackWarn}>
            {introReady
              ? 'Ready — continue when you have read this.'
              : `Read this first — continue in ${introLeft}s.`}
          </Text>
          <GlassButton
            variant="secondary"
            disabled={!introReady}
            onPress={() => {
              setError(null);
              setPhase('acks');
            }}
          >
            {introReady ? 'I understand — continue' : `Continue in ${introLeft}s`}
          </GlassButton>
        </>
      ) : null}

      {phase === 'acks' ? (
        <>
          <Text style={styles.sectionTitle}>Confirmation 2 of 3</Text>
          <Text style={styles.body}>
            Turn on every acknowledgement. This is a soft delete, never a hard delete. We keep
            account identifiers, uploads, transactions, holdings, debts, insights, household
            history, purchase records, and logs. Encrypted backups may retain copies for up to 90
            days. Household members may still see data you already shared.
          </Text>
          {ACKS.map((item) => {
            const selected = Boolean(acks[item.key]);
            return (
              <GlassButton
                key={item.key}
                variant="secondary"
                size="sm"
                selected={selected}
                onPress={() => toggleAck(item.key)}
                accessibilityRole="switch"
                accessibilityState={{ checked: selected }}
                accessibilityLabel={item.label}
              >
                {selected ? `On · ${item.label}` : `Off · ${item.label}`}
              </GlassButton>
            );
          })}
          <Text style={acksComplete ? styles.feedbackOk : styles.feedbackWarn}>
            {ackCount} of {ACKS.length} acknowledgements
            {acksComplete ? ' — continue when ready.' : ' — all three must be on.'}
          </Text>
          <GlassButton
            variant="secondary"
            disabled={!acksComplete}
            onPress={() => {
              setError(null);
              setPhase('type');
            }}
          >
            I understand records are kept — continue
          </GlassButton>
          <GlassButton
            variant="ghost"
            onPress={() => {
              setPhase('intro');
              setError(null);
            }}
          >
            Back
          </GlassButton>
        </>
      ) : null}

      {phase === 'type' ? (
        <>
          <Text style={styles.sectionTitle}>Confirmation 3 of 3</Text>
          <Text style={styles.body}>
            Type your {identityKind} and {CONFIRM_PHRASE}. The deactivate button stays locked until
            both match and {HOLD_SECONDS} seconds pass. This does not cancel a Google Play, App
            Store, or Stripe subscription.
          </Text>
          <Text style={styles.fieldLabel}>Your {identityKind}</Text>
          <TextInput
            value={identityTyped}
            onChangeText={(value) => {
              setIdentityTyped(value);
              setError(null);
            }}
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="off"
            placeholder={accountLabel || identityKind}
            placeholderTextColor={colors.textMuted}
            style={styles.confirmInput}
            accessibilityLabel={`Type your ${identityKind} to confirm`}
          />
          <Text style={identityOk ? styles.feedbackOk : styles.feedbackMuted}>{identityHint}</Text>
          <Text style={styles.fieldLabel}>{CONFIRM_PHRASE}</Text>
          <TextInput
            value={phraseTyped}
            onChangeText={(value) => {
              setPhraseTyped(value);
              setError(null);
            }}
            autoCapitalize="characters"
            autoCorrect={false}
            autoComplete="off"
            placeholder={CONFIRM_PHRASE}
            placeholderTextColor={colors.textMuted}
            style={styles.confirmInput}
            accessibilityLabel={`Type ${CONFIRM_PHRASE} to confirm`}
          />
          <Text style={phraseOk ? styles.feedbackOk : styles.feedbackMuted}>{phraseHint}</Text>
          {identityOk && phraseOk ? (
            <Text style={holdReady ? styles.feedbackOk : styles.feedbackWarn}>
              {holdReady
                ? 'Hold complete — you can deactivate.'
                : `Wait ${holdLeft}s — both fields match.`}
            </Text>
          ) : (
            <Text style={styles.feedbackMuted}>Match both fields to start the {HOLD_SECONDS}s hold.</Text>
          )}
          {error ? <Text style={styles.error}>{error}</Text> : null}
          {submitHint ? <Text style={styles.feedbackOk}>{submitHint}</Text> : null}
          <GlassButton
            variant="ghost"
            disabled={busy || !typeReady}
            onPress={() => {
              void runDelete();
            }}
            textStyle={styles.dangerLabel}
            accessibilityLabel={`Deactivate ${APP_NAME} account`}
          >
            {busy ? (
              <ActivityIndicator color={colors.danger} />
            ) : typeReady ? (
              'Deactivate my account'
            ) : identityOk && phraseOk ? (
              `Deactivate in ${holdLeft}s`
            ) : (
              'Complete both fields to unlock'
            )}
          </GlassButton>
          <GlassButton
            variant="ghost"
            disabled={busy}
            onPress={() => {
              setPhase('acks');
              setError(null);
              setSubmitHint(null);
            }}
          >
            Back
          </GlassButton>
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: spacing.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    borderRadius: radii.md,
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  accountMeta: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '600',
  },
  stepsRail: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  stepChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  stepChipIndex: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    width: 22,
    height: 22,
    textAlign: 'center',
    lineHeight: 22,
    borderRadius: 11,
    overflow: 'hidden',
    backgroundColor: colors.surfaceElevated,
  },
  stepChipIndexActive: {
    color: colors.background,
    backgroundColor: colors.warning,
  },
  stepChipIndexDone: {
    color: colors.background,
    backgroundColor: colors.accent,
  },
  stepChipLabel: {
    ...typography.caption,
    color: colors.textMuted,
  },
  stepChipLabelActive: {
    color: colors.text,
    fontWeight: '600',
  },
  sectionTitle: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
  },
  body: {
    ...typography.body,
    color: colors.textMuted,
    lineHeight: 24,
  },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.text,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  confirmInput: {
    ...typography.body,
    fontSize: 16,
    color: colors.text,
    letterSpacing: 0.4,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: colors.surfaceElevated,
  },
  error: {
    ...typography.caption,
    color: colors.danger,
  },
  feedbackOk: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '600',
  },
  feedbackWarn: {
    ...typography.caption,
    color: colors.warning,
    fontWeight: '600',
  },
  feedbackMuted: {
    ...typography.caption,
    color: colors.textMuted,
  },
  dangerLabel: {
    color: colors.danger,
    fontWeight: '700',
  },
});
